import { compact, first, isEmpty } from 'lodash';

import {
  AugmentedCreatePresentationTemplateBundleInput,
  AugmentedCreatePresentationTemplateInput,
  AugmentedCreatePresentationTemplatePageInput,
  AugmentedUpdatePresentationTemplateInput,
  PresentationBundleKind,
  PresentationTemplateType,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { isPresentationTemplateSharedFromOrganization } from '../presentationTemplate.utils';
import {
  PresentationTemplateEditor_PresentationTemplateBundleFragment,
  PresentationTemplateEditor_PresentationTemplatePageFragment,
  PresentationTemplateQuery,
} from './graphql/PresentationTemplateEditor.generated';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
  PresentationTemplateEditorPage,
} from './PresentationTemplateEditor.types';
import { isDocumentSummaryConfigIncomplete } from './stages/presentationTemplate/documentSummaryBundleConfig/DocumentSummaryBundleConfigModal.utils';
import { isEntitySummaryConfigIncomplete } from './stages/presentationTemplate/entitySummaryBundleConfig/EntitySummaryBundleConfigModal.utils';

export function mapPresentationTemplateToFormShape(
  data: PresentationTemplateQuery | undefined,
  tenantId: string
): PresentationTemplateEditorFormShape | null {
  const template = first(getNodes(data?.presentationTemplates));

  if (!template) return null;

  return {
    id: template.id,
    displayName: template.displayName,
    bundles: compact(getNodes(template.bundles)).map(mapBundle),
    isSystemTemplate: Boolean(template.systemTemplateKind),
    isSharedTemplate: isPresentationTemplateSharedFromOrganization(
      template,
      tenantId
    ),
  };
}

function mapBundle({
  pages,
  ...bundle
}: PresentationTemplateEditor_PresentationTemplateBundleFragment): PresentationTemplateEditorBundle {
  return {
    ...bundle,
    dividerTemplateBundleConfiguration:
      bundle.dividerTemplateBundleConfiguration ?? undefined,
    waterfallTemplateBundleConfiguration:
      bundle.waterfallTemplateBundleConfiguration ?? undefined,
    waterfallComparisonTemplateBundleConfiguration:
      bundle.waterfallComparisonTemplateBundleConfiguration ?? undefined,
    documentSummaryTemplateBundleConfiguration:
      bundle.documentSummaryTemplateBundleConfiguration ?? undefined,
    beneficiariesTemplateBundleConfiguration:
      bundle.beneficiariesTemplateBundleConfiguration ?? undefined,
    insightsTemplateBundleConfiguration:
      bundle.insightsTemplateBundleConfiguration ?? undefined,
    entitySummaryTemplateBundleConfiguration:
      bundle.entitySummaryTemplateBundleConfiguration ?? undefined,
    pages: compact(getNodes(pages)).map(mapPage),
  };
}

function mapPage(
  page: PresentationTemplateEditor_PresentationTemplatePageFragment
): PresentationTemplateEditorPage {
  return page;
}

export function mapFormToUpdatePresentationTemplateInput(
  formData: PresentationTemplateEditorFormShape
): AugmentedUpdatePresentationTemplateInput | null {
  const output: AugmentedUpdatePresentationTemplateInput = {
    id: formData.id,
    update: {
      displayName: formData.displayName,
      clearBundles: true,
    },
    withBundles: formData.bundles.map(mapBundleToEndpoint),
  };
  return output;
}

function mapBundleToEndpoint(
  bundle: PresentationTemplateEditorBundle,
  index: number
): AugmentedCreatePresentationTemplateBundleInput {
  return {
    create: {
      kind: bundle.kind,
      index,
      // Saving clears and recreates every bundle, so these must be echoed
      // back or they're dropped on the next write.
      templateNotes: bundle.templateNotes,
      excludeFromOutput: bundle.excludeFromOutput,
      additionalPromptContext: bundle.additionalPromptContext,
    },
    withDividerTemplateBundleConfiguration:
      bundle.dividerTemplateBundleConfiguration
        ? {
            create: {
              dividerTitle:
                bundle.dividerTemplateBundleConfiguration.dividerTitle ?? '',
              customContent:
                bundle.dividerTemplateBundleConfiguration.customContent ?? '',
            },
          }
        : undefined,
    withWaterfallTemplateBundleConfiguration:
      bundle.waterfallTemplateBundleConfiguration
        ? {
            create: {
              includeCoverPage:
                bundle.waterfallTemplateBundleConfiguration.includeCoverPage,
              includeAssumptionsPage:
                bundle.waterfallTemplateBundleConfiguration
                  .includeAssumptionsPage,
              includeSummaryPage:
                bundle.waterfallTemplateBundleConfiguration.includeSummaryPage,
              includeSummaryBars:
                bundle.waterfallTemplateBundleConfiguration.includeSummaryBars,
              includeSummaryTiles:
                bundle.waterfallTemplateBundleConfiguration.includeSummaryTiles,
              showSummaryTileDetails:
                bundle.waterfallTemplateBundleConfiguration
                  .showSummaryTileDetails,
              includeDiagramPage:
                bundle.waterfallTemplateBundleConfiguration.includeDiagramPage,
              includeTaxOverviewPage:
                bundle.waterfallTemplateBundleConfiguration
                  .includeTaxOverviewPage,
              includeOverview:
                bundle.waterfallTemplateBundleConfiguration.includeOverview,
              includeBeforeFirstDeath:
                bundle.waterfallTemplateBundleConfiguration
                  .includeBeforeFirstDeath,
              includeAfterFirstDeath:
                bundle.waterfallTemplateBundleConfiguration
                  .includeAfterFirstDeath,
              includeAfterSecondDeath:
                bundle.waterfallTemplateBundleConfiguration
                  .includeAfterSecondDeath,
              summaryHideZeroDollarItems:
                bundle.waterfallTemplateBundleConfiguration
                  .summaryHideZeroDollarItems,
            },
          }
        : undefined,
    withWaterfallComparisonTemplateBundleConfiguration:
      bundle.waterfallComparisonTemplateBundleConfiguration
        ? {
            create: {
              includeCoverPage:
                bundle.waterfallComparisonTemplateBundleConfiguration
                  .includeCoverPage,
              includeAssumptionsPage:
                bundle.waterfallComparisonTemplateBundleConfiguration
                  .includeAssumptionsPage,
            },
          }
        : undefined,
    withDocumentSummaryTemplateBundleConfiguration:
      bundle.documentSummaryTemplateBundleConfiguration
        ? {
            create: {
              documentType:
                bundle.documentSummaryTemplateBundleConfiguration.documentType,
              includeFirstGrantor:
                bundle.documentSummaryTemplateBundleConfiguration
                  .includeFirstGrantor,
              includeSecondGrantor:
                bundle.documentSummaryTemplateBundleConfiguration
                  .includeSecondGrantor,
            },
          }
        : undefined,
    withBeneficiariesTemplateBundleConfiguration:
      bundle.beneficiariesTemplateBundleConfiguration
        ? {
            create: {
              includeHouseholdOverview:
                bundle.beneficiariesTemplateBundleConfiguration
                  .includeHouseholdOverview,
              includeIndividualOverviews:
                bundle.beneficiariesTemplateBundleConfiguration
                  .includeIndividualOverviews,
            },
          }
        : undefined,
    withInsightsTemplateBundleConfiguration:
      bundle.insightsTemplateBundleConfiguration
        ? {
            create: {
              includeActiveInsights:
                bundle.insightsTemplateBundleConfiguration
                  .includeActiveInsights,
              includeMissingDocuments:
                bundle.insightsTemplateBundleConfiguration
                  .includeMissingDocuments,
              includeCompletedInsights:
                bundle.insightsTemplateBundleConfiguration
                  .includeCompletedInsights,
            },
          }
        : undefined,
    withEntitySummaryTemplateBundleConfiguration:
      bundle.entitySummaryTemplateBundleConfiguration
        ? {
            create: {
              // Required non-null on the input, and already collapsed to
              // [ALL]-or-list by the config modal's builder.
              entityTypes:
                bundle.entitySummaryTemplateBundleConfiguration.entityTypes,
              includeEntitySummaryPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeEntitySummaryPage,
              includeDiagramAndNotesPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeDiagramAndNotesPage,
              includeBalanceSheetPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeBalanceSheetPage,
              includeBalanceSheetExportPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeBalanceSheetExportPage,
              includeBeneficiariesAndDispositionsPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeBeneficiariesAndDispositionsPage,
              includeInsurancePolicySlides:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeInsurancePolicySlides,
              includeEntityNotesOnSummaryPage:
                bundle.entitySummaryTemplateBundleConfiguration
                  .includeEntityNotesOnSummaryPage,
            },
          }
        : undefined,
    withPages: bundle.pages.map(mapPageToEndpoint),
  };
}

function mapPageToEndpoint(
  page: PresentationTemplateEditorPage,
  index: number
): AugmentedCreatePresentationTemplatePageInput {
  const { customPage, coverPage } = page;
  return {
    create: {
      kind: page.kind,
      index,
    },
    withCustomPage: customPage
      ? {
          create: {
            pageName: customPage.pageName,
            pageContent: customPage.pageContent,
            includePageHeader: customPage.includePageHeader,
            pageHeader: customPage.pageHeader,
            pageSubheader: customPage.pageSubheader,
            customPrompt: customPage.customPrompt,
          },
        }
      : undefined,
    withCoverPage: coverPage
      ? {
          create: {
            customContent: coverPage.customContent,
            displayTitle: coverPage.displayTitle,
            customPrompt: coverPage.customPrompt,
          },
        }
      : undefined,
  };
}

interface SaveValidationResult {
  canSave: boolean;
  /** Reason saving is blocked, or null when valid. */
  tooltip: string | null;
}

/**
 * Returns whether the template can be saved and, if not, a user-facing
 * tooltip describing why. Requires at least one bundle and all custom pages
 * to have titles.
 */
export function canSavePresentationTemplate(
  bundles: PresentationTemplateEditorBundle[]
): SaveValidationResult {
  if (bundles.length === 0) {
    return {
      canSave: false,
      tooltip:
        'At least one page is required. Click on a page from the left to get started.',
    };
  }

  const missingCount =
    countCustomPagesMissingTitles(bundles) +
    countWaterfallBundlesMissingPages(bundles) +
    countBeneficiaryBundlesMissingPages(bundles) +
    countWaterfallComparisonBundlesMissingConfig(bundles) +
    countDocumentSummaryBundlesMissingConfig(bundles) +
    countInsightsBundlesMissingConfig(bundles) +
    countEntitySummaryBundlesMissingConfig(bundles);
  if (missingCount > 0) {
    const tooltip =
      missingCount === 1
        ? 'You still have 1 page that requires editing. You must update this page or remove it from your template in order to save.'
        : `You still have ${missingCount} pages that require editing. You must update these pages or remove them from your template in order to save.`;
    return { canSave: false, tooltip };
  }

  return { canSave: true, tooltip: null };
}

/**
 * Returns the number of custom page bundles that are missing a page title.
 */
export function countCustomPagesMissingTitles(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.CustomPageBundle) return false;
    return bundle.pages.some(
      (page) => !page.customPage || isEmpty(page.customPage.pageName)
    );
  }).length;
}

/**
 * Returns the number of waterfall bundles that have no pages selected.
 */
export function countWaterfallBundlesMissingPages(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.WaterfallOverviewBundle)
      return false;
    const config = bundle.waterfallTemplateBundleConfiguration;
    if (!config) return true;
    return !(
      config.includeCoverPage ||
      config.includeAssumptionsPage ||
      config.includeSummaryPage ||
      config.includeDiagramPage ||
      config.includeTaxOverviewPage
    );
  }).length;
}

/**
 * Returns the number of beneficiary bundles that have no pages selected.
 */
export function countBeneficiaryBundlesMissingPages(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.BeneficiaryReportBundle)
      return false;
    const config = bundle.beneficiariesTemplateBundleConfiguration;
    if (!config) return true;
    return !(
      config.includeHouseholdOverview || config.includeIndividualOverviews
    );
  }).length;
}

/**
 * Returns the number of document summary bundles that are missing required
 * configuration (document type or client selection).
 */
export function countDocumentSummaryBundlesMissingConfig(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.DocumentSummaryBundle)
      return false;
    return isDocumentSummaryConfigIncomplete(
      bundle.documentSummaryTemplateBundleConfiguration
    );
  }).length;
}

/**
 * Returns the number of waterfall comparison bundles that are missing
 * configuration (i.e. the user has not yet opened and saved the config modal).
 */
export function countWaterfallComparisonBundlesMissingConfig(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.WaterfallComparisonBundle)
      return false;
    return !bundle.waterfallComparisonTemplateBundleConfiguration;
  }).length;
}

/**
 * Returns the number of insights bundles that have no pages selected.
 */
export function countInsightsBundlesMissingConfig(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.EstateInsightsBundle)
      return false;
    const config = bundle.insightsTemplateBundleConfiguration;
    if (!config) return true;
    return !(
      config.includeActiveInsights ||
      config.includeMissingDocuments ||
      config.includeCompletedInsights
    );
  }).length;
}

/**
 * Returns the number of entity overview bundles that have no entity types or no
 * pages selected.
 */
export function countEntitySummaryBundlesMissingConfig(
  bundles: PresentationTemplateEditorBundle[]
): number {
  return bundles.filter((bundle) => {
    if (bundle.kind !== PresentationBundleKind.EntitySummaryBundle)
      return false;
    return isEntitySummaryConfigIncomplete(
      bundle.entitySummaryTemplateBundleConfiguration
    );
  }).length;
}

export function mapFormToCreatePresentationTemplateInput(
  formData: PresentationTemplateEditorFormShape,
  templateType: PresentationTemplateType
): AugmentedCreatePresentationTemplateInput {
  return {
    create: {
      displayName: formData.displayName,
      type: templateType,
    },
    withBundles: formData.bundles.map(mapBundleToEndpoint),
  };
}
